import api from '@/lib/api';
import {
  SCRIPT_AUDIO_ALIGN_PROCESS_TIMEOUT_MS,
  SCRIPT_AUDIO_ALIGN_RECOVERY_INTERVAL_MS,
  SCRIPT_AUDIO_ALIGN_RECOVERY_POLL_MS,
  isRequestTimeout,
} from '@/lib/scriptAudioAlign/apiTimeouts';

export { isRequestTimeout };

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchLatestAlignJobForStory(storyId) {
  const { data } = await api.get(`/script-audio-align/jobs/by-story/${storyId}`);
  return data;
}

/**
 * Run script→audio alignment with a long timeout and poll recovery if the browser
 * aborts while the server is still processing.
 */
export async function postScriptAudioAlignProcess(payload, { onProgress } = {}) {
  try {
    const { data } = await api.post('/script-audio-align/process', payload, {
      timeout: SCRIPT_AUDIO_ALIGN_PROCESS_TIMEOUT_MS,
    });
    return data;
  } catch (err) {
    if (!isRequestTimeout(err) || !payload?.story_id) throw err;

    const deadline = Date.now() + SCRIPT_AUDIO_ALIGN_RECOVERY_POLL_MS;
    let lastRunning = null;
    while (Date.now() < deadline) {
      await sleep(SCRIPT_AUDIO_ALIGN_RECOVERY_INTERVAL_MS);
      try {
        const job = await fetchLatestAlignJobForStory(payload.story_id);
        if (job?.status === 'completed') {
          return { ...job, recovered: true };
        }
        if (job?.status === 'failed') {
          const failure = new Error(job.error_message || 'Alignment failed');
          failure.response = {
            data: {
              detail: {
                message: job.error_message || 'Alignment failed',
                job_id: job.job_id,
                pipeline_steps: job.pipeline_steps || [],
              },
            },
          };
          throw failure;
        }
        if (job?.status === 'running') {
          lastRunning = job;
          onProgress?.(job);
        }
      } catch (pollErr) {
        if (pollErr?.response?.status === 404) continue;
        if (pollErr?.message && !pollErr?.response) throw pollErr;
      }
    }

    if (lastRunning) {
      const timeoutErr = new Error(
        'Alignment is still running on the server. Refresh this page in a minute — your job is saved.',
      );
      timeoutErr.partialResult = lastRunning;
      throw timeoutErr;
    }
    throw err;
  }
}
